export interface PacientDataState {
  name: string;
  age: string;
  sex: string;
  color: string;
  maritalStatus: string;
  weight: string;
  height: string;
  educationDegree: string;
  profession: string;
  address: string;
  cellphone: string;
  consultationDate: string;
  consultatedBy: string;
  reasonForConsultation: string;
  followUp: string;
}

export interface ClinicalDataState {
  qp: string;
  hda: string;
  hpp: string;
  familyHistory: string;
  painType: string;
  medication: string;
  hasHas: boolean;
  isSmoker: boolean;
  hasAllergy: boolean;
  hasDiabetes: boolean;
  isObese: boolean;
  hasPneumonia: boolean;
  isCardiopath: boolean;
}

export interface ClinicalPhysicalExamState {
  pa: string;
  fc: string;
  fr: string;
  instrospection: string;
  palpation: string;
  marchType: string;
  articulationAmplitude: string;
  goniometry: string;
  periometry: string;
  muscleStrength: string;
  complementaryExams: string;
}

export type BreathingPattern = 'DIAFRAGMATICO' | 'COSTAL' | 'MISTO';

export interface AvaliationDataState {
  painHappens: boolean;
  painLocation: string;
  painIntensity: string;
  painCharacteristics: string;
  painDuration: string;
  otherObservations: string;
  limitation: string;
  spasm: boolean;
  spasmLocation: string;
  fracture: boolean;
  fractureLocation: string;
  breathingType: BreathingPattern;
  cognitiveDeficit: boolean;
  auditoryDeficit: boolean;
  visualDeficit: boolean;
  characteristics: string;
}

export interface TreatmentDataState {
  fisioterapeuticDiagnosis: string;
  fisioterapeuticTreatment: string;
  observations: string;
}

export type FormData = {
  pacientData: PacientDataState;
  clinicalData: ClinicalDataState;
  clinicalPhysicalExamData: ClinicalPhysicalExamState;
  avaliationData: AvaliationDataState;
  treatmentData: TreatmentDataState;
};

export const PACIENT_DATA_INITIAL_STATE: PacientDataState = {
  name: '',
  age: '',
  sex: '',
  color: '',
  maritalStatus: '',
  weight: '',
  height: '',
  educationDegree: '',
  profession: '',
  address: '',
  cellphone: '',
  consultationDate: new Date().toISOString().split('T')[0],
  consultatedBy: '',
  reasonForConsultation: '',
  followUp: '',
};

export const CLINICAL_DATA_INITIAL_STATE: ClinicalDataState = {
  qp: '',
  hda: '',
  hpp: '',
  familyHistory: '',
  hasAllergy: false,
  hasDiabetes: false,
  hasHas: false,
  hasPneumonia: false,
  isCardiopath: false,
  isObese: false,
  isSmoker: false,
  painType: '',
  medication: '',
};

export const CLINICAL_PHYSICAL_EXAM_INITIAL_STATE: ClinicalPhysicalExamState = {
  fc: '',
  fr: '',
  articulationAmplitude: '',
  goniometry: '',
  muscleStrength: '',
  instrospection: '',
  marchType: '',
  pa: '',
  palpation: '',
  periometry: '',
  complementaryExams: '',
};

export const AVALIATION_DATA_INITIAL_STATE: AvaliationDataState = {
  breathingType: 'DIAFRAGMATICO',
  characteristics: '',
  cognitiveDeficit: false,
  fracture: false,
  fractureLocation: '',
  limitation: '',
  otherObservations: '',
  painCharacteristics: '',
  painDuration: '',
  painHappens: false,
  painIntensity: '',
  painLocation: '',
  spasm: false,
  spasmLocation: '',
  visualDeficit: false,
  auditoryDeficit: false,
};

export const TREATMENT_DATA_INITIAL_STATE: TreatmentDataState = {
  fisioterapeuticDiagnosis: '',
  fisioterapeuticTreatment: '',
  observations: '',
};

export const INITIAL_FORM_DATA: FormData = {
  pacientData: PACIENT_DATA_INITIAL_STATE,
  clinicalData: CLINICAL_DATA_INITIAL_STATE,
  clinicalPhysicalExamData: CLINICAL_PHYSICAL_EXAM_INITIAL_STATE,
  avaliationData: AVALIATION_DATA_INITIAL_STATE,
  treatmentData: TREATMENT_DATA_INITIAL_STATE,
};

/** Same merge as `FormDataContext` loadApi — baseline must use this or dirty-check breaks. */
export function mergeFormDataFromApiPayload(payload: FormData): FormData {
  return {
    pacientData: { ...INITIAL_FORM_DATA.pacientData, ...payload.pacientData },
    clinicalData: {
      ...INITIAL_FORM_DATA.clinicalData,
      ...payload.clinicalData,
    },
    clinicalPhysicalExamData: {
      ...INITIAL_FORM_DATA.clinicalPhysicalExamData,
      ...payload.clinicalPhysicalExamData,
    },
    avaliationData: {
      ...INITIAL_FORM_DATA.avaliationData,
      ...payload.avaliationData,
    },
    treatmentData: {
      ...INITIAL_FORM_DATA.treatmentData,
      ...payload.treatmentData,
    },
  };
}

/** Defaults for a brand-new report (e.g. consultation date = today). */
export function buildInitialFormData(): FormData {
  const today = new Date().toISOString().split('T')[0];
  return {
    ...INITIAL_FORM_DATA,
    pacientData: {
      ...INITIAL_FORM_DATA.pacientData,
      consultationDate: today,
    },
  };
}

const STORAGE_KEY = 'formData';

export function mergeFormDataFromStorage(raw: unknown): FormData {
  if (!raw || typeof raw !== 'object') {
    return buildInitialFormData();
  }
  const parsed = raw as Partial<FormData>;
  return {
    pacientData: {
      ...PACIENT_DATA_INITIAL_STATE,
      ...parsed.pacientData,
    },
    clinicalData: {
      ...CLINICAL_DATA_INITIAL_STATE,
      ...parsed.clinicalData,
    },
    clinicalPhysicalExamData: {
      ...CLINICAL_PHYSICAL_EXAM_INITIAL_STATE,
      ...parsed.clinicalPhysicalExamData,
    },
    avaliationData: {
      ...AVALIATION_DATA_INITIAL_STATE,
      ...parsed.avaliationData,
    },
    treatmentData: {
      ...TREATMENT_DATA_INITIAL_STATE,
      ...parsed.treatmentData,
    },
  };
}

export function loadFormDataFromStorage(): FormData {
  if (typeof window === 'undefined') {
    return buildInitialFormData();
  }
  try {
    const storageData = window.localStorage.getItem(STORAGE_KEY);
    if (!storageData) {
      return buildInitialFormData();
    }
    return mergeFormDataFromStorage(JSON.parse(storageData) as unknown);
  } catch {
    return buildInitialFormData();
  }
}

export function persistFormDataToStorage(data: FormData): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  } catch {
    // ignore quota / private mode
  }
}
